export const services = [
  {
    slug: "medicion-fincas-terrenos",
    ogImage: "/og/servicio-medicion-fincas-terrenos.jpg",
    shortName: "Medición de fincas y terrenos",
    name: "Medición de fincas y terrenos",
    category: "fincas",
    seoTitle: "Medición de fincas y terrenos en Canarias | MideFinca",
    metaDescription:
      "Medición profesional de fincas, parcelas y terrenos en Canarias con GNSS y estación total. Conoce la superficie real de tu propiedad con documentación técnica clara.",
    excerpt: "Conoce la superficie y la geometría real de tu parcela con medición GNSS y plano técnico.",
    h1: "Medición de fincas y terrenos en Canarias",
    intro:
      "Conocer la superficie real de una finca es el punto de partida para vender, comprar, repartir una herencia o corregir datos en Catastro. Medimos tu parcela con receptores GNSS y estación total, y te entregamos un plano con la cabida real, los vértices y la geometría completa.",
    when: [
      "Vas a vender o comprar y quieres saber cuántos metros tiene realmente la finca.",
      "Catastro, escritura y realidad no cuadran y necesitas una medición objetiva.",
      "Has heredado una finca y nadie sabe con exactitud su superficie ni sus límites.",
      "Necesitas una medición fiable como base para un GML, una georreferenciación o un deslinde.",
      "Quieres dividir o agrupar parcelas y necesitas conocer la cabida de partida.",
    ],
    solves:
      "La mayoría de los problemas con fincas empiezan igual: nadie sabe cuántos metros hay de verdad. Catastro dice una cifra, la escritura otra y el vallado marca otra cosa. Una medición profesional sustituye las suposiciones por datos medidos sobre el terreno, y es la base técnica sobre la que se apoya cualquier trámite posterior.",
    includes: [
      "Visita a la finca y medición con receptores GNSS y, cuando la cobertura o la precisión lo requieren, estación total.",
      "Localización y toma de coordenadas de los vértices de la parcela.",
      "Plano con la geometría medida, superficie resultante y perímetro.",
      "Memoria técnica descriptiva del trabajo realizado.",
      "Comparativa con la superficie de Catastro y de la escritura, si aportas la documentación.",
      "Certificado de medición.",
    ],
    needs: [
      "Referencia catastral de la finca (si la tienes).",
      "Escritura o nota simple, si están disponibles.",
      "Acceso a la finca el día de la medición.",
      "Cualquier indicación sobre linderos conocidos: mojones, vallados, muros.",
    ],
    receive: [
      "Plano de la finca con superficie medida, en PDF.",
      "Relación de coordenadas de los vértices.",
      "Memoria técnica con la metodología y el equipo utilizado.",
      "Comparativa de superficies: medida, catastral y registral.",
      "Certificado de medición.",
      "Informe de Validación Gráfica Alternativa (IVGA) positivo, cuando proceda.",
    ],
    faq: [
      {
        q: "¿Cuánto cuesta medir una finca?",
        a: "Depende de la superficie, la orografía, la vegetación, el acceso y la documentación previa. Por eso no publicamos tarifas genéricas: cuéntanos tu caso y te damos un presupuesto cerrado antes de empezar.",
      },
      {
        q: "¿La medición sirve para modificar Catastro?",
        a: "La medición es la base técnica que acredita la superficie real, pero cada procedimiento ante Catastro tiene sus propios requisitos. Te indicamos qué puede hacerse con tu documentación y qué pasos seguirían.",
      },
      {
        q: "¿Cuánto se tarda?",
        a: "Una finca sencilla puede medirse en una sola visita. El plazo total depende de la agenda y del tamaño del trabajo; lo confirmamos en el presupuesto.",
      },
    ],
    relatedServices: ["georreferenciacion-fincas", "gml-catastro-registro", "correccion-discrepancias-catastrales"],
    relatedProblems: ["mi-finca-tiene-mas-o-menos-metros", "no-se-donde-termina-mi-finca"],
    relatedGuides: ["como-saber-metros-reales-finca", "que-es-ivga-informe-validacion-grafica", "como-rectificar-superficie-catastro"],
  },
  {
    slug: "levantamiento-topografico",
    ogImage: "/og/servicio-levantamiento-topografico.jpg",
    shortName: "Levantamiento topográfico",
    name: "Levantamiento topográfico",
    category: "fincas",
    seoTitle: "Levantamiento topográfico en Canarias | MideFinca",
    metaDescription:
      "Levantamientos topográficos de parcelas y terrenos en Canarias: planos con curvas de nivel, cotas y elementos existentes para proyectos, obra y documentación técnica.",
    excerpt: "Planos topográficos detallados con curvas de nivel, cotas y elementos existentes, listos para proyectar.",
    h1: "Levantamiento topográfico en Canarias",
    intro:
      "Un levantamiento topográfico no se limita a representar la finca en planta: también registra las alturas o cotas del terreno y de los elementos existentes. Las cotas son, sencillamente, las alturas de cada punto medidas respecto a una referencia. Con ellas se conocen desniveles, pendientes, muros, plataformas, edificaciones y demás elementos necesarios para proyectar con precisión, y el resultado es un plano topográfico listo para diseñar sobre una realidad medida, no supuesta.",
    when: [
      "Un arquitecto o ingeniero va a proyectar sobre tu parcela y te pide un levantamiento.",
      "Necesitas conocer pendientes, cotas y desniveles antes de edificar o acondicionar.",
      "Quieres documentar el estado actual de una finca con todos sus elementos.",
      "Vas a presentar un proyecto y la administración exige plano topográfico georreferenciado.",
    ],
    solves:
      "Proyectar sobre planos antiguos o aproximados sale caro: errores de cota, muros que no están donde deberían, pendientes mal estimadas. El levantamiento topográfico da al proyectista una base medida y georreferenciada, y evita sorpresas en obra.",
    includes: [
      "Trabajo de campo con GNSS y estación total.",
      "Toma de cotas y elementos: edificaciones, muros, caminos, arbolado relevante, redes visibles.",
      "Plano topográfico con curvas de nivel y escala adecuada al uso.",
      "Georreferenciación del plano en el sistema oficial (ETRS89 / UTM huso 28).",
      "Entrega en PDF y formatos CAD (DWG/DXF) si se solicitan.",
    ],
    needs: [
      "Localización y referencia catastral de la parcela.",
      "Acceso al terreno el día del trabajo de campo.",
      "Indicación del uso previsto del plano (proyecto, obra, documentación), para ajustar escala y detalle.",
    ],
    receive: [
      "Plano topográfico georreferenciado en PDF.",
      "Archivo CAD (DWG/DXF) si lo necesita tu proyectista.",
      "Curvas de nivel, cotas y elementos existentes representados.",
      "Certificado de medición.",
    ],
    faq: [
      {
        q: "¿En qué se diferencia de una medición de finca?",
        a: "La medición de finca se centra en la superficie y los límites. El levantamiento topográfico añade el relieve: cotas, curvas de nivel y todos los elementos del terreno, pensado para proyectar.",
      },
      {
        q: "¿El plano vale para presentar un proyecto?",
        a: "Es la base que habitualmente requieren los proyectos, entregada georreferenciada. Los requisitos concretos de cada administración pueden variar; si tu arquitecto indica especificaciones, las seguimos.",
      },
    ],
    relatedServices: ["medicion-fincas-terrenos", "georreferenciacion-fincas"],
    relatedProblems: ["no-se-donde-termina-mi-finca"],
    relatedGuides: [],
  },
  {
    slug: "georreferenciacion-fincas",
    ogImage: "/og/servicio-georreferenciacion-fincas.jpg",
    shortName: "Georreferenciación de fincas",
    name: "Georreferenciación de fincas",
    category: "fincas",
    seoTitle: "Georreferenciación de fincas en Canarias | MideFinca",
    metaDescription:
      "Georreferenciación de fincas y parcelas en Canarias: coordenadas oficiales de los vértices de tu propiedad, base para GML, Catastro y representación gráfica registral.",
    excerpt: "Tus vértices en coordenadas oficiales: la base para GML, Catastro, Notaría, Registro de la Propiedad y cualquier trámite gráfico.",
    h1: "Georreferenciación de fincas en Canarias",
    intro:
      "Georreferenciar una finca es situar sus vértices en coordenadas oficiales (ETRS89, huso 28 en Canarias), de modo que su geometría quede anclada al territorio y no a descripciones ambiguas. Es la base técnica de los archivos GML y de cualquier representación gráfica ante Catastro o el Registro.",
    when: [
      "Te han pedido la georreferenciación de la finca para una escritura o un trámite.",
      "Vas a aportar un GML a Catastro o al Registro y necesitas coordenadas medidas.",
      "La representación gráfica catastral no coincide con la realidad y quieres acreditar la geometría correcta.",
      "Necesitas relacionar tu parcela con la cartografía oficial sin margen de duda.",
    ],
    solves:
      "Las escrituras antiguas describen fincas por colindancias («al norte, tierras de…») que hoy no localizan nada. La georreferenciación traduce la propiedad a coordenadas medidas, comparables con Catastro y defendibles en cualquier expediente.",
    includes: [
      "Medición de los vértices de la finca con GNSS y estación total.",
      "Cálculo y ajuste de coordenadas en el sistema de referencia oficial.",
      "Plano georreferenciado de la parcela.",
      "Relación de coordenadas de vértices, lista para generar el GML si se encarga.",
    ],
    needs: [
      "Referencia catastral.",
      "Escritura o nota simple, si existe.",
      "Acceso a la finca e indicación de los límites conocidos.",
    ],
    receive: [
      "Plano georreferenciado con la geometría de la finca.",
      "Tabla de coordenadas de los vértices en ETRS89 / UTM 28.",
      "Si lo encargas, el archivo GML validado para Catastro y Registro.",
      "Informe detallado.",
      "Certificado de medición.",
      "Informe de Validación Gráfica Alternativa (IVGA) positivo, cuando proceda.",
    ],
    faq: [
      {
        q: "¿Georreferenciar es lo mismo que medir?",
        a: "Van juntos: primero se miden los vértices sobre el terreno y después se calculan sus coordenadas oficiales. La georreferenciación es el resultado de anclar esa medición al sistema cartográfico.",
      },
      {
        q: "¿Modifica esto algo en Catastro automáticamente?",
        a: "No. La georreferenciación es un documento técnico. Modificar datos catastrales requiere su propio procedimiento, en el que esta documentación suele ser la prueba técnica de partida.",
      },
    ],
    relatedServices: ["gml-catastro-registro", "medicion-fincas-terrenos", "correccion-discrepancias-catastrales"],
    relatedProblems: ["necesito-gml", "catastro-escritura-no-coinciden"],
    relatedGuides: ["que-es-un-archivo-gml", "que-es-ivga-informe-validacion-grafica"],
  },
  {
    slug: "gml-catastro-registro",
    ogImage: "/og/servicio-gml-catastro-registro.jpg",
    shortName: "GML para Catastro y Registro",
    name: "Archivo GML para Catastro y Registro",
    category: "fincas",
    seoTitle: "GML para Catastro y Registro en Canarias | MideFinca",
    metaDescription:
      "Elaboración de archivos GML de fincas en Canarias conforme a las especificaciones de Catastro: para escrituras, notarías, Registro de la Propiedad y representación gráfica.",
    excerpt: "El archivo GML que te piden en notaría o Registro, elaborado con medición real y validado.",
    h1: "Archivo GML para Catastro y Registro en Canarias",
    intro:
      "El GML es el archivo con la geometría georreferenciada de tu finca que notarías y registradores reclaman desde la Ley 13/2015 para incorporar la representación gráfica a la escritura. Lo elaboramos a partir de medición real y lo validamos conforme a las especificaciones de la Dirección General de Catastro.",
    when: [
      "El notario te ha pedido un GML para firmar una compraventa, herencia o segregación.",
      "El registrador exige la representación gráfica georreferenciada de la finca.",
      "Vas a declarar obra nueva y necesitas la geometría de la parcela y de la construcción.",
      "Quieres que Catastro dibuje tu finca donde y como realmente es.",
    ],
    solves:
      "Un GML mal hecho, dibujado sobre ortofoto sin medir, con topologías incorrectas o sin validar, se rechaza y retrasa la firma. Nosotros partimos de la medición real de la finca, construimos la geometría conforme a las especificaciones catastrales y la validamos antes de entregarla.",
    includes: [
      "Medición y georreferenciación de los vértices de la finca.",
      "Generación del archivo GML conforme a las especificaciones de Catastro.",
      "Validación del archivo en las herramientas oficiales.",
      "Plano de la representación gráfica resultante.",
    ],
    needs: [
      "Referencia catastral de la finca.",
      "Escritura o requerimiento del notario/registrador, si lo tienes.",
      "Acceso a la finca para la medición.",
    ],
    receive: [
      "Archivo GML validado, listo para aportar en notaría o Registro.",
      "Plano con la representación gráfica de la finca.",
      "Relación de coordenadas utilizada.",
      "Informe de Validación Gráfica Alternativa (IVGA) positivo, cuando proceda.",
    ],
    faq: [
      {
        q: "¿La notaría aceptará este GML?",
        a: "Elaboramos el GML conforme a las especificaciones oficiales y lo validamos con las herramientas de Catastro. Su incorporación al expediente corresponde a la Notaría y al Registro de la Propiedad, pero el archivo cumple el formato que exigen.",
      },
      {
        q: "¿El mismo archivo GML sirve para Catastro y Registro de la Propiedad?",
        a: "El formato GML permite representar técnicamente la geometría georreferenciada de una parcela. La documentación que debe acompañarlo depende del trámite concreto y, en determinados procedimientos, puede ser necesario obtener además un Informe de Validación Gráfica Alternativa positivo. Revisamos el caso antes de preparar la documentación.",
      },
      {
        q: "¿Cuánto tarda?",
        a: "Depende de la agenda de medición y de la complejidad de la finca. Lo confirmamos en el presupuesto. Si tienes prevista una fecha de firma en Notaría o cualquier otro plazo concreto, coméntanoslo desde el primer contacto.",
      },
    ],
    relatedServices: ["georreferenciacion-fincas", "medicion-fincas-terrenos", "declaracion-obra-nueva"],
    relatedProblems: ["necesito-gml", "catastro-escritura-no-coinciden"],
    relatedGuides: ["que-es-un-archivo-gml", "que-es-ivga-informe-validacion-grafica"],
  },
  {
    slug: "linderos-deslindes",
    ogImage: "/og/servicio-linderos-deslindes.jpg",
    shortName: "Linderos y deslindes",
    name: "Comprobación de linderos y deslindes",
    category: "fincas",
    seoTitle: "Linderos y deslindes de fincas en Canarias | MideFinca",
    metaDescription:
      "Comprobación y replanteo de linderos de fincas en Canarias. Localizamos los límites reales de tu parcela con medición técnica y documentación clara.",
    excerpt: "Localiza y marca los límites reales de tu finca con medición técnica, no con suposiciones.",
    h1: "Comprobación de linderos y deslindes en Canarias",
    intro:
      "Saber dónde empieza y dónde acaba una finca parece sencillo hasta que necesitas localizarla físicamente sobre el terreno. Descargamos la representación catastral de la parcela y, durante la visita, replanteamos sus vértices para que puedas identificar sobre el terreno dónde se sitúan los puntos que figuran en la cartografía catastral. Si existe una discrepancia entre Catastro, escritura, Registro, vallados o la realidad física, el trabajo puede requerir un estudio adicional de linderos y documentación: la cartografía catastral por sí sola no determina necesariamente el límite jurídico de una propiedad.",
    when: [
      "No sabes con exactitud dónde están los límites de tu finca.",
      "Un vecino ha movido un vallado o discute el lindero.",
      "Vas a cerrar, vallar o edificar y necesitas certeza sobre los límites antes de gastar.",
      "Los hitos o mojones han desaparecido y hay que replantearlos.",
    ],
    solves:
      "Este servicio permite localizar físicamente hasta dónde representa Catastro que llega tu finca, identificar sus vértices sobre el terreno y disponer de referencias para realizar posteriormente un vallado o señalización. También permite detectar posibles diferencias entre la realidad física y la representación catastral y saber si es necesario realizar después un trabajo de medición o deslinde más completo.",
    includes: [
      "Visita a la finca.",
      "Descarga de la parcela y cartografía catastral disponible.",
      "Replanteo in situ de los vértices de la representación catastral.",
      "Plano de la representación replanteada con sus coordenadas.",
      "Informe técnico con las diferencias detectadas, si las hubiera.",
    ],
    needs: [
      "Escritura o nota simple de la finca.",
      "Referencia catastral.",
      "Acceso a la parcela y, si es posible, a los límites por ambos lados.",
    ],
    receive: [
      "Vértices localizados durante la visita, para que puedas materializar o identificar físicamente los puntos mediante tu propia señalización.",
      "Plano de la representación catastral con las coordenadas de los vértices.",
      "Informe técnico con las diferencias detectadas entre la cartografía y la realidad física, si las hubiera.",
    ],
    faq: [
      {
        q: "¿Podéis obligar al vecino a aceptar el lindero?",
        a: "No. Nuestro trabajo es técnico: medimos y documentamos dónde está el límite según la documentación y la medición. La aceptación del vecino o un eventual deslinde judicial son vías distintas, en las que intervienen otros profesionales.",
      },
      {
        q: "¿Y si la medición no coincide con el vallado actual?",
        a: "Es frecuente. Entregamos el dato medido y la comparativa; a partir de ahí, la solución puede pasar por un acuerdo entre propietarios o por la vía judicial, y la documentación técnica es la base en ambos casos.",
      },
      {
        q: "¿Quién coloca físicamente las marcas de los vértices?",
        a: "El día de la visita, el propietario debe disponer de los elementos con los que quiera señalizar los puntos: varillas, estacas, bloques, piedras u otro sistema adecuado. Nosotros localizamos cada vértice con el equipo de medición y el propietario puede ir materializando los puntos sobre el terreno.",
      },
      {
        q: "¿Los puntos catastrales son siempre el lindero jurídico de mi propiedad?",
        a: "No necesariamente. Catastro contiene una representación gráfica de la parcela, pero pueden existir diferencias con la escritura, el Registro de la Propiedad, los límites físicos existentes o la realidad medida. Si detectamos una discrepancia, te indicaremos qué estudio o trámite puede ser necesario para aclararla.",
      },
    ],
    relatedServices: ["medicion-fincas-terrenos", "georreferenciacion-fincas"],
    relatedProblems: ["no-se-donde-termina-mi-finca"],
    relatedGuides: [],
  },
  {
    slug: "correccion-discrepancias-catastrales",
    ogImage: "/og/servicio-correccion-discrepancias-catastrales.jpg",
    shortName: "Corrección de discrepancias catastrales",
    name: "Corrección de discrepancias catastrales",
    category: "fincas",
    seoTitle: "Corrección de discrepancias catastrales en Canarias | MideFinca",
    metaDescription:
      "¿Catastro no coincide con tu finca real? Estudiamos la discrepancia de superficie o geometría y elaboramos la documentación técnica para acreditar la realidad medida.",
    excerpt: "Estudio técnico completo cuando Catastro, escritura y realidad no cuadran.",
    h1: "Corrección de discrepancias catastrales en Canarias",
    intro:
      "Que Catastro dibuje tu finca con otros metros o en otro sitio es más habitual de lo que parece. Estudiamos el origen de la discrepancia (medición antigua, errores de cartografía o escrituras por cabida aparente) y elaboramos la documentación técnica que acredita la realidad medida.",
    when: [
      "Catastro atribuye a tu finca más o menos metros de los reales.",
      "La representación gráfica catastral está desplazada o no se parece a tu parcela.",
      "La escritura y Catastro dicen cosas distintas y no sabes cuál refleja la realidad.",
      "Te han denegado o complicado un trámite por datos catastrales incorrectos.",
    ],
    solves:
      "Antes de corregir nada hay que saber qué dato es el correcto. Medimos la finca, reconstruimos el historial documental y comparamos las tres fuentes: realidad, Catastro y título. Con ese diagnóstico sabrás qué procedimiento puede corresponder y qué prueba técnica necesitas.",
    includes: [
      "Medición y georreferenciación de la finca.",
      "Estudio comparativo: medición real vs. Catastro vs. escritura.",
      "Informe técnico con el diagnóstico de la discrepancia y su origen probable.",
      "Documentación gráfica (plano y, si procede, GML) para acreditar la realidad medida.",
      "Elaboración de la representación gráfica necesaria y obtención de Informe de Validación Gráfica Alternativa (IVGA) positivo cuando sea necesario para plantear la corrección catastral.",
    ],
    needs: [
      "Referencia catastral.",
      "Escritura y, si las tienes, escrituras anteriores o títulos antiguos.",
      "Acceso a la finca.",
    ],
    receive: [
      "Informe técnico de la discrepancia con planos comparativos.",
      "Medición georreferenciada de la finca.",
      "Orientación sobre los cauces que pueden corresponder según el caso.",
      "Certificado de medición.",
      "Planos georreferenciados.",
      "Informe de Validación Gráfica Alternativa (IVGA) positivo, cuando proceda.",
    ],
    faq: [
      {
        q: "¿Conseguiréis que Catastro cambie los datos?",
        a: "Nosotros realizamos la medición, elaboramos la documentación técnica y preparamos la propuesta gráfica necesaria para solicitar la corrección. A partir de ahí, corresponde a Catastro tramitar el procedimiento y resolverlo. Cuando el procedimiento afecta a parcelas colindantes, Catastro puede realizar las comunicaciones o actuaciones que legalmente correspondan respecto de los titulares afectados. La decisión final corresponde siempre a la Administración.",
      },
      {
        q: "¿Y si el que está mal es mi escritura?",
        a: "También ocurre: muchas escrituras antiguas se otorgaron por cabida aparente. El informe identifica qué fuente se aparta de la realidad medida y, a partir de ahí, valoramos contigo los escenarios posibles.",
      },
    ],
    relatedServices: ["medicion-fincas-terrenos", "gml-catastro-registro", "georreferenciacion-fincas"],
    relatedProblems: ["catastro-escritura-no-coinciden", "mi-finca-tiene-mas-o-menos-metros"],
    relatedGuides: ["por-que-catastro-y-escritura-no-coinciden", "como-saber-metros-reales-finca", "que-es-ivga-informe-validacion-grafica", "como-rectificar-superficie-catastro"],
  },
  {
    slug: "comprobacion-finca-antes-comprar",
    ogImage: "/og/servicio-comprobacion-finca-antes-comprar.jpg",
    shortName: "Comprobación de finca antes de comprar",
    name: "Comprobación de finca antes de comprar",
    category: "fincas",
    seoTitle: "Comprobar una finca antes de comprar en Canarias | MideFinca",
    metaDescription:
      "Compra una finca en Canarias sabiendo lo que compras: medición de superficie real, comprobación de linderos y comparativa con Catastro y escritura antes de firmar.",
    excerpt: "Mide y verifica la finca antes de firmar: superficie real, límites y coherencia documental.",
    h1: "Comprobación de finca antes de comprar en Canarias",
    intro:
      "Comprar una finca sin saber cuántos metros tiene de verdad es firmar a ciegas. Antes de la compra medimos la parcela, comprobamos sus límites y comparamos la realidad con Catastro y con la escritura, para que sepas exactamente lo que estás comprando.",
    when: [
      "Vas a comprar una finca rústica o una parcela y quieres verificar los metros anunciados.",
      "El anuncio, la escritura y Catastro dicen cifras distintas.",
      "Quieres saber dónde están realmente los límites antes de pagar.",
      "Compras para edificar o proyectar y necesitas datos reales del terreno.",
    ],
    solves:
      "Las diferencias de superficie se descubren casi siempre después de comprar, cuando corregirlas es más caro y ya no hay margen de negociación. Una comprobación previa te da datos para negociar el precio, exigir correcciones al vendedor o descartar la operación a tiempo.",
    includes: [
      "Medición de la finca y comprobación de su superficie real.",
      "Revisión de la coherencia entre realidad, Catastro y escritura.",
      "Comprobación de linderos y detección de posibles ocupaciones o invasión de colindantes.",
      "Informe técnico con conclusiones claras para tu decisión de compra.",
    ],
    needs: [
      "Referencia catastral o dirección de la finca.",
      "Autorización o acompañamiento del vendedor o de la inmobiliaria para acceder.",
      "Documentación disponible: nota simple, escritura del vendedor.",
    ],
    receive: [
      "Informe de comprobación con la superficie medida y las discrepancias detectadas.",
      "Plano de la finca con linderos medidos.",
      "Conclusiones y puntos a revisar antes de firmar.",
    ],
    faq: [
      {
        q: "¿Necesito permiso del vendedor?",
        a: "Sí, para medir sobre el terreno necesitamos acceso a la finca, que suele gestionar el vendedor o la inmobiliaria. Es una petición razonable: un vendedor que la rechaza está dando información valiosa.",
      },
      {
        q: "¿Cuánto tarda la comprobación?",
        a: "En fincas sencillas, el trabajo de campo suele resolverse en una visita y el informe en pocos días. Si tienes fecha de firma de arras o escritura, indícala al contactar.",
      },
    ],
    relatedServices: ["medicion-fincas-terrenos", "linderos-deslindes", "levantamiento-topografico"],
    relatedProblems: ["comprar-finca-comprobar-superficie"],
    relatedGuides: ["como-saber-metros-reales-finca"],
  },
{
    slug: "segregacion-finca-canarias",
    ogImage: "/og/servicio-segregacion-finca-canarias.jpg",
    shortName: "Segregación de finca",
    name: "Segregación de finca",
    category: "fincas",
    seoTitle: "Segregación de fincas en Canarias | MideFinca",
    metaDescription:
      "Documentación técnica para segregar una finca en Canarias: definición de las nuevas parcelas, medición, coordenadas y representación gráfica para Ayuntamiento, Catastro, Notaría y Registro, según corresponda.",
    excerpt: "Dividimos una finca en dos o más parcelas independientes, con sus nuevas geometrías, superficies y coordenadas definidas técnicamente.",
    h1: "Segregación de fincas en Canarias",
    intro:
      "Dividimos una finca existente en dos o más parcelas independientes, definiendo técnicamente sus nuevas geometrías, superficies y coordenadas. Revisamos la documentación disponible y preparamos la representación gráfica necesaria para los trámites ante Ayuntamiento, Catastro, Notaría y Registro de la Propiedad, según corresponda.",
    when: [
      "Quieres repartir una finca entre herederos o copropietarios.",
      "Vas a vender solo una parte de tu parcela.",
      "Necesitas separar una porción de la finca para una operación concreta.",
      "La finca actual resulta demasiado grande para el uso que le quieres dar.",
    ],
    solves:
      "Una segregación empieza por saber exactamente qué se divide y cómo: dónde pasan los nuevos linderos, qué superficie resulta en cada parcela y qué coordenadas tienen los nuevos vértices. Sin esa definición técnica previa, el trámite notarial, registral o catastral no tiene sobre qué trabajar.",
    includes: [
      "Revisión de la documentación disponible: escritura, Registro y Catastro.",
      "Medición y georreferenciación de la finca actual.",
      "Definición técnica de las nuevas parcelas: geometrías, superficies y linderos.",
      "Planos de cada parcela resultante.",
      "Representación gráfica y archivo GML de las nuevas parcelas, cuando proceda.",
      "Informe de Validación Gráfica Alternativa (IVGA) positivo, cuando proceda.",
    ],
    needs: [
      "Escritura de la finca y referencia catastral.",
      "Nota simple del Registro, si la tienes.",
      "Indicación del reparto previsto entre las nuevas parcelas.",
      "Acceso a la finca para la medición.",
    ],
    receive: [
      "Informe técnico con la definición de las nuevas parcelas.",
      "Planos de cada parcela resultante con su superficie.",
      "Documentación georreferenciada y GML, cuando proceda.",
      "Informe de Validación Gráfica Alternativa (IVGA) positivo, cuando proceda.",
    ],
    faq: [
      {
        q: "¿Toda finca puede segregarse?",
        a: "No. La segregación queda condicionada al cumplimiento de la normativa urbanística y a las autorizaciones administrativas que sean necesarias en cada municipio. Por eso estudiamos la finca antes de confirmar que la operación es viable: no damos por hecho que una segregación pueda hacerse sin haberla comprobado.",
      },
      {
        q: "¿Quién interviene en una segregación?",
        a: "Según el caso: el Ayuntamiento cuando la normativa urbanística exige autorización o comunicación, la Notaría para la escritura de la división, el Registro de la Propiedad para la inscripción de las fincas resultantes y Catastro para la actualización gráfica. Nuestra parte es la documentación técnica y la representación gráfica.",
      },
      {
        q: "¿Cuántas parcelas pueden salir de mi finca?",
        a: "Depende de la normativa urbanística aplicable, de la superficie mínima exigible, de los frentes y accesos disponibles y de las condiciones de la finca. Lo comprobamos en la revisión inicial antes de proponerte nada.",
      },
    ],
    relatedServices: ["agrupacion-fincas-canarias", "medicion-fincas-terrenos", "correccion-discrepancias-catastrales"],
    relatedProblems: ["dividir-finca-en-parcelas"],
    relatedGuides: ["como-segregar-una-finca-en-canarias"],
  },
  {
    slug: "agrupacion-fincas-canarias",
    ogImage: "/og/servicio-agrupacion-fincas-canarias.jpg",
    shortName: "Agrupación de fincas",
    name: "Agrupación de fincas",
    category: "fincas",
    seoTitle: "Agrupación de fincas en Canarias | MideFinca",
    metaDescription:
      "Documentación técnica para agrupar varias fincas en Canarias: nueva geometría y superficie de la finca resultante y representación gráfica para Catastro, Notaría y Registro, cuando corresponda.",
    excerpt: "Unimos dos o más fincas en una única finca resultante, con su nueva geometría, superficie y representación gráfica.",
    h1: "Agrupación de fincas en Canarias",
    intro:
      "Unimos dos o más fincas en una única finca resultante, definiendo su nueva geometría, superficie y representación gráfica. Comprobamos la documentación disponible y elaboramos la documentación técnica necesaria para coordinar la operación con Catastro, Notaría y Registro de la Propiedad cuando corresponda.",
    when: [
      "Tienes dos o más parcelas colindantes y quieres gestionarlas como una sola.",
      "Compraste la finca vecina y quieres unirla a la tuya.",
      "Necesitas una finca única para una operación o un proyecto concreto.",
      "Quieres simplificar la documentación de varias parcelas de tu propiedad.",
    ],
    solves:
      "Agrupar fincas no es sumar superficies sobre el papel: hay que definir la geometría de la finca resultante, comprobar que las parcelas son colindantes y compatibles, y documentar la nueva realidad jurídica y gráfica de la propiedad para que Catastro, la escritura y el Registro reflejen lo mismo.",
    includes: [
      "Revisión de la documentación de las fincas: escrituras, Registro y Catastro.",
      "Medición y georreferenciación del conjunto.",
      "Definición de la geometría y superficie de la finca resultante.",
      "Planos de la situación anterior y de la finca agrupada.",
      "Representación gráfica y archivo GML, cuando proceda.",
      "Informe de Validación Gráfica Alternativa (IVGA) positivo, cuando proceda.",
    ],
    needs: [
      "Escrituras y referencias catastrales de las fincas a agrupar.",
      "Notas simples del Registro, si las tienes.",
      "Acceso a las parcelas para la medición.",
    ],
    receive: [
      "Informe técnico de la agrupación.",
      "Planos de la finca resultante con su nueva superficie.",
      "Documentación georreferenciada y GML, cuando proceda.",
      "Informe de Validación Gráfica Alternativa (IVGA) positivo, cuando proceda.",
    ],
    faq: [
      {
        q: "¿Todas las fincas pueden agruparse?",
        a: "No automáticamente. Hay que comprobar que sean colindantes, revisar la titularidad y las cargas registrales y comprobar la normativa aplicable. Lo estudiamos antes de confirmar la viabilidad de la operación.",
      },
      {
        q: "¿Quién formaliza la agrupación?",
        a: "La agrupación se formaliza normalmente mediante escritura ante Notaría y se inscribe en el Registro de la Propiedad; después se actualiza la representación en Catastro. Nosotros aportamos la medición, los planos y la representación gráfica que esos trámites necesitan.",
      },
    ],
    relatedServices: ["segregacion-finca-canarias", "medicion-fincas-terrenos", "gml-catastro-registro"],
    relatedProblems: ["unir-varias-fincas"],
    relatedGuides: ["como-agrupar-fincas-en-canarias"],
  },
  {
    slug: "declaracion-obra-nueva",
    ogImage: "/og/servicio-declaracion-obra-nueva.jpg",
    shortName: "Obra nueva por antigüedad",
    name: "Declaración de obra nueva por antigüedad",
    category: "edificaciones",
    seoTitle: "Declaración de obra nueva por antigüedad en Canarias | MideFinca",
    metaDescription:
      "Documentación técnica para declaración de obra nueva por antigüedad en Canarias: medición, planos, informe técnico, certificado de medición, antigüedad y documentación gráfica para Catastro y Registro.",
    excerpt: "Medición, planos e informe técnico de tu construcción para la declaración de obra nueva por antigüedad, cuando se cumplan sus requisitos.",
    h1: "Declaración de obra nueva por antigüedad en Canarias",
    intro:
      "Tienes una construcción que no figura correctamente en la escritura: una vivienda, un cuarto de aperos, un salón, un local, una casa en finca rústica, un porche u otra edificación. Cuando se cumplen los requisitos legales, la declaración de obra nueva por antigüedad permite documentar técnicamente la construcción para su posterior formalización notarial e inscripción registral. El trabajo puede incluir medición, planos, informe técnico, certificado de medición, acreditación de antigüedad y la documentación gráfica necesaria para Catastro y Registro. En viviendas, según el caso, puede ser necesaria además documentación relativa a las condiciones de habitabilidad.",
    when: [
      "Existe una construcción en tu finca que no aparece en la escritura ni en Catastro.",
      "Vas a vender y el comprador o su banco piden que la construcción conste documentada.",
      "Has heredado una finca con edificaciones no declaradas.",
      "Quieres ordenar la documentación de tu propiedad antes de que surja el problema.",
    ],
    solves:
      "La declaración de obra nueva la otorga el notario y la inscribe el Registro; la parte que nos corresponde es la técnica: medir la construcción, dibujarla, georreferenciarla y elaborar el certificado que describe qué hay, cuánto mide y desde cuándo consta que existe, según las evidencias disponibles.",
    includes: [
      "Informe técnico.",
      "Medición de la construcción y de la parcela.",
      "Planos de la edificación: superficie, huella y posición en la parcela.",
      "Certificado de medición.",
      "Certificado de antigüedad, cuando proceda.",
      "Documentación de habitabilidad, en viviendas y cuando corresponda.",
      "Informe de Validación Gráfica Alternativa (IVGA) positivo o documentación gráfica catastral equivalente, cuando proceda.",
    ],
    needs: [
      "Escritura de la finca y referencia catastral.",
      "Acceso a la finca y a la construcción.",
      "Cualquier documento que acredite la antigüedad de la obra (fotos antiguas, facturas, licencias), si existe.",
    ],
    receive: [
      "Informe técnico.",
      "Certificado de medición.",
      "Certificado de antigüedad.",
      "Certificado o documentación de habitabilidad, cuando corresponda.",
      "Planos.",
      "Documentación georreferenciada.",
      "Informe de Validación Gráfica Alternativa (IVGA) positivo, cuando proceda.",
    ],
    faq: [
      {
        q: "¿Declaración de obra nueva por antigüedad y «prescripción» son lo mismo?",
        a: "Coloquialmente es habitual hablar de «prescribir una construcción», pero técnicamente es más correcto hablar de declaración de obra nueva por antigüedad y de la eventual limitación temporal de las potestades de restablecimiento de la legalidad urbanística. Para poder utilizar esta vía hay que estudiar la antigüedad, la situación urbanística del inmueble y comprobar que no se encuentra en alguno de los supuestos en los que legalmente no opera esa limitación temporal.",
      },
      {
        q: "¿Declarar una obra nueva por antigüedad significa legalizarla?",
        a: "No. Son cuestiones distintas. La legalización es un procedimiento urbanístico mediante el que se pretende acreditar o conseguir que una actuación se ajuste a la normativa urbanística aplicable. La declaración de obra nueva por antigüedad es un procedimiento destinado, cuando se cumplen sus requisitos, a documentar e incorporar una edificación al título y al Registro de la Propiedad. Su inscripción registral no convierte automáticamente la construcción en urbanísticamente legal.",
      },
      {
        q: "¿Cuándo puede tramitarse una declaración de obra nueva por antigüedad?",
        a: "Hay que poder acreditar una fecha determinada de terminación de la construcción y comprobar que ha transcurrido el plazo que resulte legalmente aplicable sin que procedan medidas de restablecimiento que impidan esta vía. En Canarias existe con carácter general un plazo de cuatro años para determinadas actuaciones, pero hay excepciones en las que la potestad de restablecimiento no está sometida a plazo. Por eso revisamos cada finca antes de confirmar que el procedimiento es viable.",
      },
      {
        q: "¿Cómo se acredita la antigüedad?",
        a: "La antigüedad puede acreditarse mediante los medios admitidos legalmente. Las ortofotografías históricas son una fuente especialmente útil porque permiten comprobar de forma objetiva si la construcción ya existía en una fecha determinada, aunque no son el único medio posible.",
      },
      {
        q: "¿Y si todavía no ha transcurrido el plazo aplicable?",
        a: "Si todavía no ha transcurrido el plazo necesario para utilizar la vía de la obra nueva por antigüedad, no podremos tramitarla por este procedimiento en ese momento. Podemos estudiar el inmueble y comprobar si existe otra vía urbanística o registral aplicable.",
      },
    ],
    relatedServices: ["ampliacion-obra-nueva", "certificado-antiguedad", "gml-catastro-registro"],
    relatedProblems: ["construccion-no-documentada"],
    relatedGuides: [],
  },
  {
    slug: "ampliacion-obra-nueva",
    ogImage: "/og/servicio-ampliacion-obra-nueva.jpg",
    shortName: "Ampliación de obra nueva",
    name: "Documentación técnica para ampliación de obra nueva",
    category: "edificaciones",
    seoTitle: "Ampliación de obra nueva en Canarias | MideFinca",
    metaDescription:
      "¿Ampliaste tu vivienda y no figura en la escritura? Medición, planos y documentación técnica para la ampliación de obra nueva en Canarias.",
    excerpt: "Tu ampliación medida, dibujada y documentada para que pueda incorporarse a la escritura.",
    h1: "Documentación para ampliación de obra nueva en Canarias",
    intro:
      "Cerraste una terraza, añadiste un dormitorio, levantaste una segunda planta o un anexo… y en la escritura y en Catastro la vivienda sigue teniendo los metros de antes. La ampliación de obra nueva es el cauce para documentar esos metros, y empieza por medirlos y dibujarlos correctamente.",
    when: [
      "Tu vivienda tiene más metros construidos de los que figuran en escritura o Catastro.",
      "Vas a vender o hipotecar y la realidad no coincide con la documentación.",
      "Quieres actualizar la superficie construida declarada de tu inmueble.",
    ],
    solves:
      "Mientras la ampliación no conste, el inmueble documentado y el real son dos cosas distintas, y eso complica ventas, valoraciones y herencias. Elaboramos la medición y el certificado técnico que la notaría necesita para tramitar la ampliación; la escritura y la inscripción corresponden a notario y Registro.",
    includes: [
      "Informe técnico.",
      "Medición completa de la vivienda, incluida la parte ampliada.",
      "Planos actualizados con la superficie construida real.",
      "Certificado técnico descriptivo de la ampliación.",
      "Certificado de medición.",
      "Informe de Validación Gráfica Alternativa (IVGA) positivo o documentación gráfica equivalente, cuando proceda.",
    ],
    needs: [
      "Escritura de la vivienda y referencia catastral.",
      "Acceso a todas las estancias, incluidas las ampliadas.",
      "Documentación de la época de la ampliación, si la conservas.",
    ],
    receive: [
      "Informe técnico.",
      "Certificado de medición.",
      "Certificado de antigüedad.",
      "Certificado o documentación de habitabilidad, cuando corresponda.",
      "Planos.",
      "Documentación georreferenciada.",
      "Informe de Validación Gráfica Alternativa (IVGA) positivo, cuando proceda.",
    ],
    faq: [
      {
        q: "¿Puedo declarar una ampliación hecha hace muchos años?",
        a: "En muchos casos sí, y la antigüedad acreditable es una pieza clave del expediente. La viabilidad y los requisitos dependen del caso; la medición y el certificado son la base en cualquiera de ellos.",
      },
      {
        q: "¿Qué ocurre si la ampliación está en un inmueble en división horizontal?",
        a: "Cuando el inmueble forma parte de una división horizontal, hay que revisar el título constitutivo, los estatutos de la comunidad y qué elementos o superficies resultan afectados por la ampliación. Si la modificación afecta a elementos comunes, cuotas de participación o a la descripción contenida en el título constitutivo, puede ser necesaria la autorización de la comunidad y la correspondiente formalización notarial. Revisamos previamente la documentación para indicarte qué consentimiento será necesario en tu caso.",
      },
      {
        q: "¿Catastro se actualiza solo después?",
        a: "No automáticamente. Tras la escritura suele presentarse la alteración en Catastro con la documentación correspondiente. Te explicamos cómo encaja cada paso.",
      },
    ],
    relatedServices: ["declaracion-obra-nueva", "certificado-antiguedad", "medicion-fincas-terrenos"],
    relatedProblems: ["construccion-no-documentada", "mi-finca-tiene-mas-o-menos-metros"],
    relatedGuides: [],
  },
  {
    slug: "division-horizontal-canarias",
    shortName: "División horizontal",
    name: "División horizontal de inmuebles",
    category: "edificaciones",
    seoTitle: "División horizontal en Canarias | MideFinca",
    ogImage: "/og/servicio-division-horizontal-canarias.jpg",
    metaDescription:
      "Medición y documentación técnica para constituir o modificar la división horizontal de un edificio en Canarias: descripción de cada unidad, superficies, elementos comunes y cuotas de participación.",
    excerpt: "La base técnica para dividir un edificio en unidades independientes: medición, descripción, superficies y cuotas.",
    h1: "División horizontal de inmuebles en Canarias",
    intro:
      "Un edificio puede dividirse jurídicamente en unidades independientes (viviendas, locales, trasteros o plazas de garaje) mediante la división horizontal. MideFinca prepara la parte técnica: medimos el inmueble y cada unidad, describimos sus superficies y elementos comunes y calculamos las cuotas de participación, de modo que notario y Registro trabajen sobre datos medidos y coherentes.",
    when: [
      "Vas a dividir un edificio existente en varias unidades independientes para venderlas o repartirlas.",
      "Constituyes el régimen de propiedad horizontal de un edificio nuevo o existente.",
      "Hay que modificar la división horizontal existente: nuevas unidades, cambios de superficie o de cuotas.",
      "La realidad construida no coincide con lo descrito en el título constitutivo.",
      "Una herencia, compraventa o aportación necesita delimitar cada unidad con precisión.",
    ],
    solves:
      "La división horizontal exige describir cada unidad con exactitud: superficie, distribución, elementos comunes y cuota de participación. Si esos datos se redactan sin medición real, aparecen discrepancias que después bloquean ventas, hipotecas o repartos. Nuestro trabajo es que la descripción técnica coincida con la realidad medida.",
    includes: [
      "Medición completa del edificio y de cada una de sus unidades, in situ.",
      "Cómputo de superficie útil y construida de cada unidad, con la parte de elementos comunes que corresponda.",
      "Descripción técnica de cada unidad y de los elementos comunes del inmueble.",
      "Cálculo de las cuotas de participación según el criterio que corresponda al caso.",
      "Planos de cada planta con la delimitación de las unidades.",
    ],
    needs: [
      "Acceso a todas las unidades y zonas comunes del edificio.",
      "Escritura, título constitutivo o nota simple, si existen.",
      "Planos o licencias previas, si las hay.",
      "La finalidad del trabajo: constituir, modificar o comprobar la división.",
    ],
    receive: [
      "Informe técnico.",
      "Planos de plantas con las unidades delimitadas y sus superficies.",
      "Memoria de superficies y cuotas de participación por unidad.",
      "Documentación gráfica georreferenciada, cuando proceda.",
    ],
    faq: [
      {
        q: "¿Quién formaliza la división horizontal?",
        a: "La constitución o modificación se formaliza en escritura pública ante notario y se inscribe en el Registro de la Propiedad. MideFinca prepara la documentación técnica (medición, descripción de unidades, superficies y cuotas) sobre la que se redacta ese título.",
      },
      {
        q: "¿Las cuotas de participación se pueden elegir libremente?",
        a: "La cuota se fija normalmente en relación con la superficie o el valor de cada unidad respecto al total, salvo que el título establezca otro criterio. Calculamos las cuotas a partir de la medición real y explicamos el criterio aplicado; la redacción jurídica final corresponde al notario.",
      },
      {
        q: "¿Sirve para un edificio antiguo que nunca se dividió?",
        a: "Sí: medimos el edificio tal como está construido y documentamos cada unidad existente. Si además hay discrepancias con Catastro o con el Registro, te indicamos qué trámites complementarios podrían ser necesarios según el caso.",
      },
    ],
    relatedServices: ["certificado-superficies", "declaracion-obra-nueva", "ampliacion-obra-nueva"],
    relatedProblems: ["dividir-edificio-en-unidades"],
    relatedGuides: ["que-es-division-horizontal"],
  },
  {
    slug: "certificado-antiguedad",
    ogImage: "/og/servicio-certificado-antiguedad.jpg",
    shortName: "Certificado de antigüedad",
    name: "Certificado de antigüedad de construcciones",
    category: "edificaciones",
    seoTitle: "Certificado de antigüedad de construcciones en Canarias | MideFinca",
    metaDescription:
      "Certificado técnico de antigüedad de edificaciones en Canarias: medición, fotointerpretación histórica y evidencias documentales para acreditar desde cuándo existe una construcción.",
    excerpt: "Acredita técnicamente desde cuándo existe una construcción con medición y evidencias históricas.",
    h1: "Certificado de antigüedad de construcciones en Canarias",
    intro:
      "Acreditar la antigüedad de una construcción es determinante en muchos expedientes: declaraciones de obra nueva, expedientes de regularización y compraventas. Elaboramos certificados técnicos que estudian las evidencias históricas de la construcción, como la fotografía aérea de distintas épocas, la cartografía antigua y la documentación aportada, para determinar desde cuándo consta la edificación.",
    when: [
      "Necesitas demostrar que una construcción existe desde hace años.",
      "Te lo piden para una declaración o ampliación de obra nueva.",
      "Un expediente administrativo o una compraventa requiere acreditar la fecha de construcción.",
      "Catastro o la escritura no recogen una edificación que lleva décadas en pie.",
    ],
    solves:
      "La antigüedad no se declara: se acredita. Nuestro certificado documenta el estado actual medido y lo cruza con ortofotos históricas y otras fuentes, de modo que la conclusión sobre la antigüedad quede apoyada en evidencias técnicas y no en afirmaciones.",
    includes: [
      "Estudio de fotografía aérea histórica y cartografía de distintas épocas.",
      "Análisis de la documentación aportada por el propietario.",
      "Certificado técnico con las evidencias y la conclusión sobre la antigüedad.",
      "Si el encargo requiere además medir la construcción, la medición se presupuesta como trabajo complementario.",
    ],
    needs: [
      "Referencia catastral y escritura del inmueble.",
      "Acceso a la construcción.",
      "Cualquier prueba de época: fotos, facturas, licencias, recibos antiguos.",
    ],
    receive: [
      "Certificado técnico de antigüedad con la conclusión documentada.",
      "Anexos con las evidencias históricas analizadas.",
    ],
    faq: [
      {
        q: "¿El certificado garantiza que la construcción quede regularizada?",
        a: "El certificado acredita técnicamente la antigüedad según las evidencias disponibles. Los efectos jurídicos o administrativos de esa antigüedad dependen de la normativa aplicable y de cada expediente; en ellos intervienen otros profesionales y administraciones.",
      },
      {
        q: "¿Qué pasa si las fotos aéreas no llegan tan atrás como necesito?",
        a: "Lo indicamos con claridad en el certificado: la conclusión se limita a lo que las evidencias permiten afirmar. Un certificado serio dice hasta dónde llega la prueba, no hasta dónde convendría que llegara.",
      },
    ],
    relatedServices: ["declaracion-obra-nueva", "ampliacion-obra-nueva"],
    relatedProblems: ["construccion-no-documentada"],
    relatedGuides: [],
  },
  {
    slug: "certificado-habitabilidad",
    officialLink: {
      label: "Texto oficial del Decreto 117/2006, de 1 de agosto (Boletín Oficial de Canarias):",
      href: "https://www.gobiernodecanarias.org/boc/2006/161/001.html",
    },
    ogImage: "/og/servicio-certificado-habitabilidad.jpg",
    shortName: "Certificado de habitabilidad",
    name: "Certificado de habitabilidad y medición de vivienda",
    category: "edificaciones",
    seoTitle: "Certificado de habitabilidad en Canarias | MideFinca",
    metaDescription:
      "Medición de vivienda y certificado técnico de habitabilidad en Canarias: superficies útiles y construidas, condiciones de la vivienda y documentación técnica para tus gestiones.",
    excerpt: "Medición real de tu vivienda y certificado técnico de sus condiciones de habitabilidad.",
    h1: "Certificado de habitabilidad y medición de vivienda en Canarias",
    intro:
      "En Canarias, las condiciones de habitabilidad de las viviendas se regulan mediante el Decreto 117/2006, de 1 de agosto. La norma establece las condiciones mínimas que deben cumplir las viviendas y diferencia distintos supuestos según la antigüedad del inmueble y el tipo de intervención: dependiendo del caso puede resultar de aplicación el Anexo I o el Anexo II del Decreto. Nuestro certificado describe técnicamente la vivienda tal como es, con sus superficies útiles y construidas, la distribución y las condiciones de iluminación y ventilación de las estancias, conforme a esa norma.",
    when: [
      "Te solicitan acreditar las condiciones de habitabilidad desde Urbanismo, Notaría u otro organismo dentro de un trámite relacionado con la vivienda.",
    ],
    solves:
      "Este certificado aporta la documentación técnica necesaria para determinados trámites de primera ocupación ante Urbanismo y la documentación de habitabilidad que pueda ser necesaria para la contratación o regularización de determinados suministros, como agua o electricidad, según el procedimiento aplicable. Además, sustituye las cifras de superficie que circulan sin haberse medido nunca por una medición in situ de la vivienda, estancia por estancia, con plano y memoria de las condiciones observadas.",
    includes: [
      "Informe técnico descriptivo.",
      "Medición completa de la vivienda in situ.",
      "Cómputo de superficie útil y construida según los criterios aplicables.",
      "Planos de la vivienda con la distribución medida.",
      "Certificado descriptivo de las condiciones de habitabilidad observadas.",
    ],
    needs: [
      "Referencia catastral y escritura o nota simple.",
      "Acceso a todas las estancias de la vivienda.",
      "Indicación del trámite para el que se necesita, para ajustar el contenido del certificado.",
    ],
    receive: [
      "Informe técnico.",
      "Certificado de habitabilidad con las condiciones observadas.",
      "Planos.",
    ],
    faq: [
      {
        q: "¿Este certificado es la «cédula de habitabilidad» oficial?",
        a: "No. La cédula de habitabilidad, donde existe, es un documento administrativo que emite la administración competente. Nuestro certificado es el documento técnico de medición y condiciones que suelen requerir esos y otros expedientes.",
      },
      {
        q: "¿Qué diferencia hay entre el Anexo I y el Anexo II del Decreto 117/2006?",
        a: "El Decreto distingue supuestos según la antigüedad del inmueble y el tipo de intervención, y cada anexo recoge condiciones distintas. Qué anexo resulta de aplicación depende del caso concreto: lo comprobamos durante la revisión de tu vivienda antes de emitir la documentación.",
      },
      {
        q: "¿Cuándo puede necesitarse este certificado?",
        a: "Habitualmente cuando Urbanismo, Notaría u otro organismo lo solicita dentro de un trámite relacionado con la vivienda, o cuando un procedimiento de contratación o regularización de suministros requiere documentación de habitabilidad.",
      },
      {
        q: "¿Qué se revisa durante la visita?",
        a: "Se mide la vivienda estancia por estancia, se comprueban la distribución y las condiciones de iluminación y ventilación, y se documenta el estado observado. Con esos datos elaboramos el informe técnico y el certificado.",
      },
    ],
    relatedServices: ["ampliacion-obra-nueva", "declaracion-obra-nueva"],
    relatedProblems: ["mi-finca-tiene-mas-o-menos-metros"],
    relatedGuides: [],
  },
  {
    slug: "certificado-superficies",
    shortName: "Certificado de superficies",
    name: "Certificado de superficies de inmuebles",
    category: "edificaciones",
    seoTitle: "Certificado de superficies en Canarias | MideFinca",
    ogImage: "/og/servicio-certificado-superficies.jpg",
    metaDescription:
      "Certificado técnico de superficie útil y construida de viviendas, locales y edificios en Canarias. Medición real in situ para compraventas, valoraciones y discrepancias.",
    excerpt: "La superficie útil y construida real de tu inmueble, medida in situ y certificada.",
    h1: "Certificado de superficies de inmuebles en Canarias",
    intro:
      "El anuncio dice una cifra, Catastro otra y la escritura una tercera. El certificado de superficies pone orden: medimos el inmueble in situ, estancia por estancia, y certificamos su superficie útil y construida con un plano que respalda cada número.",
    when: [
      "Vas a vender y quieres anunciar los metros que realmente tiene el inmueble.",
      "Compras o has comprado y la superficie real no coincide con lo pactado.",
      "Una valoración, tasación o peritaje necesita una medición fiable de partida.",
      "Catastro o la escritura recogen una superficie que no se corresponde con la realidad.",
      "Necesitas el cómputo de superficie para una división, segregación o reparto.",
    ],
    solves:
      "La mayoría de las cifras de superficie que circulan nunca se han medido: provienen de planos antiguos, de declaraciones aproximadas o de arrastres entre documentos. El certificado de superficies las sustituye por una medición real, con el criterio de cómputo explicado y la geometría dibujada.",
    includes: [
      "Medición completa del inmueble in situ, estancia por estancia.",
      "Cómputo de superficie útil y construida según los criterios aplicables, con elementos comunes si procede.",
      "Plano de la distribución medida con las superficies por estancia.",
      "Certificado técnico con el resultado de la medición y el criterio empleado.",
      "Comparativa con la superficie de Catastro y de la escritura, si aportas la documentación.",
    ],
    needs: [
      "Acceso a todas las estancias del inmueble.",
      "Referencia catastral.",
      "Escritura o nota simple, si quieres la comparativa documental.",
      "Indicación de la finalidad del certificado, para ajustar su contenido.",
    ],
    receive: [
      "Informe técnico.",
      "Certificado de superficies con el desglose por estancias.",
      "Plano de la distribución medida.",
      "Comparativa de superficies: medida, catastral y registral, si procede.",
    ],
    faq: [
      {
        q: "¿Qué diferencia hay entre superficie útil y construida?",
        a: "La útil es la que puedes pisar y usar; la construida añade muros, instalaciones y, según el caso, la parte proporcional de elementos comunes. El certificado indica ambas y el criterio con el que se han computado.",
      },
      {
        q: "¿Este certificado cambia la superficie en Catastro?",
        a: "No automáticamente. Es la prueba técnica de la superficie real; modificar Catastro requiere su propio procedimiento, en el que este documento suele ser la base.",
      },
      {
        q: "¿Puedo usarlo para reclamar metros en una compraventa?",
        a: "Es la medición objetiva del inmueble, que es la prueba técnica de partida. La reclamación en sí y su viabilidad corresponden valorarlas a tu abogado.",
      },
    ],
    relatedServices: ["certificado-habitabilidad", "correccion-discrepancias-catastrales", "ampliacion-obra-nueva"],
    relatedProblems: ["mi-finca-tiene-mas-o-menos-metros", "catastro-escritura-no-coinciden"],
    relatedGuides: ["como-saber-metros-reales-finca"],
  },
  {
    slug: "certificado-estructural",
    shortName: "Certificado estructural",
    name: "Certificado del estado estructural de edificaciones",
    category: "edificaciones",
    seoTitle: "Certificado estructural de edificaciones en Canarias | MideFinca",
    ogImage: "/og/servicio-certificado-estructural.jpg",
    metaDescription:
      "Informe técnico del estado estructural de viviendas y edificios en Canarias: inspección, lesiones observadas, valoración de su alcance y recomendaciones antes de comprar o reformar.",
    excerpt: "Valoración técnica del estado de la estructura: lesiones observadas, alcance y recomendaciones.",
    h1: "Certificado del estado estructural en Canarias",
    intro:
      "Una fisura puede ser un problema de pintura o el síntoma de algo serio. El certificado del estado estructural documenta cómo está la estructura del inmueble (pilares, vigas, forjados, muros y cimentación visible), qué lesiones presenta y qué alcance tienen, para que decidas con información técnica y no con intuiciones.",
    when: [
      "Vas a comprar un inmueble antiguo y quieres conocer el estado de su estructura.",
      "Han aparecido fisuras, grietas, deformaciones o desprendimientos y no sabes si son graves.",
      "Planeas una reforma que afecta a elementos estructurales y necesitas conocer el punto de partida.",
      "Una administración, aseguradora o comunidad de propietarios te requiere un informe del estado del edificio.",
      "Tras un siniestro, necesitas documentar técnicamente los daños estructurales.",
    ],
    solves:
      "La incertidumbre sobre una estructura paraliza decisiones: compras que no se cierran, reformas que no se atreven a empezar, discusiones sobre la gravedad de una grieta. El certificado la sustituye por una inspección técnica documentada que distingue lo estético de lo estructural y marca los pasos razonables a seguir.",
    includes: [
      "Visita e inspección visual de los elementos estructurales accesibles.",
      "Descripción del sistema estructural del inmueble.",
      "Registro fotográfico y localización de las lesiones observadas: fisuras, grietas, deformaciones, humedades o corrosión.",
      "Valoración técnica del alcance de las lesiones y de la situación general.",
      "Recomendaciones: actuaciones, vigilancia o, cuando proceda, ensayos o un estudio estructural detallado.",
    ],
    needs: [
      "Acceso a todas las zonas del inmueble, incluidas cubierta, sótano o zonas comunes si procede.",
      "Documentación del edificio, si existe: proyecto, planos, informes anteriores.",
      "Indicación del motivo del encargo, para orientar la inspección.",
    ],
    receive: [
      "Certificado técnico con el estado estructural observado y las lesiones documentadas.",
      "Valoración del alcance de las lesiones y recomendaciones de actuación.",
      "Anexo fotográfico y gráfico de la inspección.",
    ],
    faq: [
      {
        q: "¿Este certificado garantiza que la estructura no tiene problemas?",
        a: "Documenta lo observado en una inspección visual: certifica el estado aparente y las lesiones detectadas, con su valoración. Si las evidencias aconsejan ensayos, apertura de catas o un cálculo estructural, lo indicamos expresamente en las recomendaciones.",
      },
      {
        q: "¿Es lo mismo que la ITE?",
        a: "No. La Inspección Técnica de Edificios, donde es exigible, es un documento administrativo con contenido reglado. Este certificado es un informe técnico del estado estructural; si lo que necesitas es la ITE, indícanoslo y te confirmamos la viabilidad del encargo.",
      },
      {
        q: "¿Sirve para una reclamación a un seguro o a un constructor?",
        a: "Es un documento técnico objetivo que describe las lesiones y su valoración, habitual como prueba de partida en reclamaciones. La estrategia jurídica corresponde a tu abogado.",
      },
    ],
    relatedServices: ["certificado-antiguedad", "certificado-habitabilidad", "declaracion-obra-nueva"],
    relatedProblems: [],
    relatedGuides: [],
  },
  {
    slug: "certificado-eficiencia-energetica",
    shortName: "Certificado de eficiencia energética",
    name: "Certificado de eficiencia energética",
    category: "edificaciones",
    seoTitle: "Certificado de eficiencia energética en Canarias | MideFinca",
    ogImage: "/og/servicio-certificado-eficiencia-energetica.jpg",
    metaDescription:
      "Certificado de eficiencia energética de viviendas y locales en Canarias: calificación energética, etiqueta y registro oficial para vender o alquilar tu inmueble.",
    excerpt: "La calificación energética oficial de tu inmueble, con etiqueta y registro, para vender o alquilar.",
    h1: "Certificado de eficiencia energética en Canarias",
    intro:
      "Para vender o alquilar una vivienda o un local necesitas el certificado de eficiencia energética: el documento que califica el inmueble de la A a la G según su consumo y sus emisiones. Visitamos el inmueble, tomamos los datos de su envolvente y de sus instalaciones, y elaboramos y registramos el certificado para que tengas la etiqueta a tiempo.",
    when: [
      "Vas a vender un inmueble y la notaría te pedirá la etiqueta energética.",
      "Vas a alquilar y el anuncio debe incluir la calificación.",
      "Tu certificado anterior ha caducado o se ha quedado desactualizado tras una reforma.",
      "Quieres conocer la calificación real de tu vivienda y qué mejoras la subirían.",
      "Solicitud de determinadas subvenciones o ayudas relacionadas con eficiencia energética, cuando las bases reguladoras exijan un certificado energético.",
    ],
    solves:
      "Sin certificado no hay anuncio correcto ni firma: es un documento obligatorio y su ausencia puede retrasar o encarecer la operación. Lo preparamos con la visita y los datos reales del inmueble, lo registramos en el órgano competente y te entregamos la etiqueta lista para anunciar y para la firma.",
    includes: [
      "Visita al inmueble y toma de datos: cerramientos, huecos, instalaciones y orientación.",
      "Cálculo de la calificación energética con los procedimientos oficiales.",
      "Certificado de eficiencia energética con calificación, consumo y emisiones.",
      "Propuesta de medidas de mejora, conforme al contenido reglamentario.",
      "Registro del certificado en el órgano competente de Canarias y obtención de la etiqueta.",
    ],
    needs: [
      "Acceso a todo el inmueble, incluidas las instalaciones.",
      "Referencia catastral y datos registrales básicos.",
      "Documentación de reformas o instalaciones recientes, si existe.",
    ],
    receive: [
      "Certificado de eficiencia energética registrado.",
      "Etiqueta energética para el anuncio y la firma.",
      "Relación de medidas de mejora valoradas para el inmueble.",
    ],
    faq: [
      {
        q: "¿Es obligatorio para vender o alquilar?",
        a: "Sí, con carácter general la normativa vigente lo exige para la venta y el arrendamiento de inmuebles, salvo excepciones tasadas. Si crees que tu caso puede estar exento, consúltanoslo antes de descartarlo.",
      },
      {
        q: "¿Cuánto tiempo tiene validez?",
        a: "El certificado tiene una vigencia máxima de diez años, y conviene actualizarlo si se han hecho reformas que cambian la calificación. Te indicamos si el tuyo sigue vigente.",
      },
      {
        q: "¿Quién puede firmarlo?",
        a: "Técnicos con la titulación habilitante que exige la normativa; en nuestro caso, la firma corresponde a la dirección técnica colegiada identificada en el aviso legal de esta web.",
      },
    ],
    relatedServices: ["certificado-habitabilidad", "certificado-superficies", "comprobacion-finca-antes-comprar"],
    relatedProblems: [],
    relatedGuides: [],
  },
  {
    slug: "primera-ocupacion-fuera-ordenacion",
    ogImage: "/og/servicio-primera-ocupacion-fuera-ordenacion.jpg",
    shortName: "Primera ocupación fuera de ordenación",
    name: "Primera ocupación en edificaciones fuera de ordenación",
    category: "edificaciones",
    seoTitle: "Primera ocupación en edificaciones fuera de ordenación | MideFinca",
    metaDescription:
      "Documentación técnica para tramitar la primera ocupación de edificaciones fuera de ordenación en Canarias, cuando el régimen urbanístico del inmueble lo permita. Estudio previo del caso.",
    excerpt: "Documentación técnica para la primera ocupación de edificaciones fuera de ordenación, cuando el régimen urbanístico lo permita.",
    h1: "Primera ocupación en edificaciones fuera de ordenación",
    intro:
      "Preparamos la documentación técnica necesaria para tramitar la primera ocupación o utilización de edificaciones que se encuentren en situación de fuera de ordenación, cuando el régimen urbanístico del inmueble permita realizar dicho trámite. Estudiamos los antecedentes disponibles, el estado actual de la edificación y las condiciones técnicas exigibles antes de preparar la documentación.",
    when: [
      "Te solicitan documentación de primera ocupación o utilización para un trámite del inmueble.",
      "Necesitas acreditar las condiciones de la edificación para la contratación o regularización de suministros.",
      "Vas a vender y te piden acreditar la situación y las condiciones de la edificación.",
      "Tu edificación está en situación de fuera de ordenación y necesitas documentar su estado actual.",
    ],
    solves:
      "Cuando una edificación está fuera de ordenación, cualquier trámite exige documentar con precisión qué existe, desde cuándo y en qué condiciones está. El estudio técnico previo determina si el régimen urbanístico del inmueble permite el trámite de primera ocupación y qué documentación puede exigirse en tu caso.",
    includes: [
      "Estudio de los antecedentes y de la documentación disponible del inmueble.",
      "Visita y comprobación del estado actual de la edificación.",
      "Medición de la edificación, cuando proceda.",
      "Informe técnico descriptivo.",
      "Documentación de habitabilidad, en viviendas y cuando corresponda.",
      "Acreditación de la antigüedad, cuando proceda.",
    ],
    needs: [
      "Escritura y referencia catastral del inmueble.",
      "La documentación existente: licencias antiguas, expedientes, planos.",
      "Acceso completo a la edificación.",
    ],
    receive: [
      "Informe técnico del estado actual y los antecedentes del inmueble.",
      "Los certificados que procedan según el caso: medición, antigüedad o habitabilidad.",
      "Planos de la edificación.",
    ],
    faq: [
      {
        q: "¿Se garantiza la concesión de la primera ocupación?",
        a: "No. La resolución corresponde al Ayuntamiento competente, que valora el expediente conforme a la normativa urbanística aplicable. Nuestro trabajo es preparar la documentación técnica que el trámite requiere, después de comprobar que el régimen urbanístico del inmueble permite intentarlo.",
      },
      {
        q: "¿Qué significa que una edificación está «fuera de ordenación»?",
        a: "Que no se ajusta a la ordenación urbanística vigente, normalmente porque se construyó con otra normativa o sin ella. Esa situación condiciona qué trámites y qué intervenciones son posibles sobre el inmueble, y debe estudiarse individualmente.",
      },
      {
        q: "¿Qué documentación puede pedir el Ayuntamiento?",
        a: "Depende del municipio y del caso: suele incluir informe técnico, planos del estado actual, certificado de antigüedad y, en viviendas, documentación de habitabilidad. Lo comprobamos antes de preparar el expediente.",
      },
    ],
    relatedServices: ["informe-uso-consolidado", "certificado-habitabilidad", "certificado-antiguedad"],
    relatedProblems: ["primera-ocupacion-fuera-de-ordenacion"],
    relatedGuides: ["primera-ocupacion-edificaciones-fuera-de-ordenacion"],
  },
  {
    slug: "informe-uso-consolidado",
    ogImage: "/og/servicio-informe-uso-consolidado.jpg",
    shortName: "Informe de uso consolidado",
    name: "Informe para declaración de uso consolidado",
    category: "edificaciones",
    seoTitle: "Informe para declaración de uso consolidado en Canarias | MideFinca",
    metaDescription:
      "Documentación técnica para acreditar las características, antigüedad y condiciones del uso existente de una edificación en Canarias, cuando sea necesario acreditar un uso consolidado.",
    excerpt: "Documentación técnica de las características, antigüedad y condiciones del uso existente de tu edificación.",
    h1: "Informe para declaración de uso consolidado",
    intro:
      "Elaboramos la documentación técnica necesaria para acreditar las características, antigüedad y condiciones del uso existente de una edificación cuando sea necesario tramitar o acreditar un uso consolidado conforme a la normativa urbanística aplicable.",
    when: [
      "Una administración o un procedimiento te pide acreditar el uso consolidado del inmueble.",
      "Necesitas demostrar que la edificación se usa de una forma determinada desde hace años.",
      "Vas a vender o regularizar y el uso actual debe quedar documentado técnicamente.",
      "El uso real del inmueble no coincide con el que figura en la documentación.",
    ],
    solves:
      "Acreditar un uso consolidado exige demostrar con evidencias qué uso tiene la edificación, desde cuándo y en qué condiciones. El informe técnico documenta esa realidad de forma objetiva para que el reconocimiento administrativo, cuando sea necesario, tenga una base técnica sólida.",
    includes: [
      "Estudio de la documentación y los antecedentes del inmueble.",
      "Visita y comprobación del uso actual de la edificación.",
      "Medición y planos del estado actual, cuando proceda.",
      "Análisis de evidencias históricas del uso: fotografía aérea y documentación de época.",
      "Informe técnico con las características, antigüedad y condiciones del uso existente.",
    ],
    needs: [
      "Escritura y referencia catastral del inmueble.",
      "Documentación que acredite el uso a lo largo del tiempo, si la tienes: contratos, recibos, licencias antiguas.",
      "Acceso completo a la edificación.",
    ],
    receive: [
      "Informe técnico del uso existente con las evidencias analizadas.",
      "Planos del estado actual, cuando proceda.",
      "Anexos documentales y fotográficos.",
    ],
    faq: [
      {
        q: "¿Qué es un uso consolidado?",
        a: "En términos generales, un uso que viene existiendo de forma continuada desde una situación urbanística anterior y que la normativa puede llegar a reconocer. Su existencia debe estudiarse individualmente y su reconocimiento, cuando sea necesario, corresponde a la Administración.",
      },
      {
        q: "¿El informe garantiza el reconocimiento del uso?",
        a: "No. El informe documenta técnicamente la realidad del uso con evidencias. El reconocimiento administrativo del uso consolidado, cuando proceda, corresponde a la Administración competente conforme a la normativa urbanística aplicable.",
      },
    ],
    relatedServices: ["primera-ocupacion-fuera-ordenacion", "certificado-antiguedad", "certificado-habitabilidad"],
    relatedProblems: ["acreditar-uso-consolidado"],
    relatedGuides: ["que-es-uso-consolidado-canarias"],
  },
  {
    slug: "cedula-urbanistica",
    ogImage: "/og/servicio-cedula-urbanistica.jpg",
    shortName: "Cédula urbanística",
    name: "Cédula urbanística",
    category: "edificaciones",
    seoTitle: "Solicitud de cédula urbanística en Canarias | MideFinca",
    metaDescription:
      "Preparamos y tramitamos la solicitud de cédula urbanística en Canarias: clasificación del suelo, parámetros, usos y condiciones de edificación de una parcela o inmueble.",
    excerpt: "Preparamos y tramitamos la solicitud de información urbanística oficial sobre tu parcela o inmueble.",
    h1: "Cédula urbanística en Canarias",
    intro:
      "Preparamos y gestionamos la documentación necesaria para solicitar información urbanística oficial sobre una parcela o inmueble: clasificación y categorización del suelo, parámetros urbanísticos, usos, condiciones de edificación y demás determinaciones que resulten aplicables.",
    when: [
      "Quieres saber qué puedes construir o qué usos admite una parcela antes de comprarla o proyectar.",
      "Una notaría, un banco o una administración te pide información urbanística oficial del inmueble.",
      "Necesitas conocer la clasificación del suelo y los parámetros aplicables a tu parcela.",
      "Vas a iniciar un proyecto y quieres confirmar las condiciones urbanísticas de partida.",
    ],
    solves:
      "Antes de comprar, proyectar o construir conviene saber qué dice el planeamiento sobre la parcela: qué se puede edificar, con qué parámetros y qué usos se admiten. La cédula o información urbanística es el documento oficial que lo acredita, y su solicitud requiere identificar correctamente el inmueble y su documentación.",
    includes: [
      "Identificación y documentación de la parcela: referencia catastral, representación gráfica y delimitación.",
      "Preparación y presentación de la solicitud de cédula urbanística o información urbanística ante el Ayuntamiento.",
      "Revisión de la respuesta municipal y explicación de su contenido en términos claros.",
      "Comprobación de la coherencia entre la información urbanística y la realidad de la parcela.",
    ],
    needs: [
      "Referencia catastral de la parcela.",
      "Escritura o nota simple, si la tienes.",
      "La finalidad de la consulta, para orientar la solicitud.",
    ],
    receive: [
      "La solicitud presentada con la documentación técnica correspondiente.",
      "La información urbanística emitida por el Ayuntamiento, una vez resuelta.",
      "Una explicación clara de lo que esa información significa para tu parcela.",
    ],
    faq: [
      {
        q: "¿MideFinca emite la cédula urbanística?",
        a: "No. La cédula urbanística o información urbanística la expide el Ayuntamiento competente. Nosotros preparamos y tramitamos la solicitud, aportamos la documentación técnica de la parcela y te explicamos el contenido de la respuesta municipal.",
      },
      {
        q: "¿Cuánto tarda el Ayuntamiento en responder?",
        a: "Depende del municipio y de su carga de expedientes. No podemos garantizar plazos administrativos; sí dejar la solicitud correctamente preparada desde el primer momento.",
      },
      {
        q: "¿Sirve para saber si puedo construir?",
        a: "Es el documento oficial que informa de la clasificación del suelo, los usos admitidos y los parámetros de edificación. Es la referencia de partida, aunque la viabilidad concreta de un proyecto se confirma con su proyecto técnico y su licencia.",
      },
    ],
    relatedServices: ["alineaciones-rasantes", "licencia-obra-mayor", "medicion-fincas-terrenos"],
    relatedProblems: ["que-puedo-construir-en-parcela"],
    relatedGuides: ["que-es-cedula-urbanistica"],
  },
  {
    slug: "alineaciones-rasantes",
    ogImage: "/og/servicio-alineaciones-rasantes.jpg",
    shortName: "Alineaciones y rasantes",
    name: "Alineaciones y rasantes",
    category: "edificaciones",
    seoTitle: "Alineaciones y rasantes en Canarias | MideFinca",
    metaDescription:
      "Documentación técnica y topográfica para definir o solicitar oficialmente la alineación de una parcela respecto de la vía y las rasantes que condicionan la futura edificación en Canarias.",
    excerpt: "Documentación técnica y topográfica para definir o solicitar la alineación de tu parcela y las cotas de rasante.",
    h1: "Alineaciones y rasantes en Canarias",
    intro:
      "Preparamos la documentación técnica y topográfica necesaria para definir o solicitar oficialmente la alineación de una parcela respecto de la vía y las cotas o rasantes que condicionan la futura edificación.",
    when: [
      "Vas a construir y el proyecto o el Ayuntamiento requiere conocer la alineación oficial.",
      "Necesitas saber a qué cota debe quedar la rasante de la futura edificación.",
      "Te piden las alineaciones y rasantes dentro de un trámite de licencia.",
      "La parcela linda con la vía pública y hay que definir su posición respecto a ella.",
    ],
    solves:
      "La alineación define hasta dónde puede llegar la edificación respecto de la vía y la rasante fija la cota de referencia sobre la que se construye. Conocerlas con precisión y con respaldo oficial es imprescindible para proyectar y para que la licencia no se retrase por errores de posición o de cota.",
    includes: [
      "Levantamiento topográfico de la parcela y de su entorno inmediato respecto de la vía.",
      "Documentación técnica y topográfica para la definición o solicitud de alineaciones y rasantes.",
      "Preparación y presentación de la solicitud ante el Ayuntamiento, cuando proceda.",
      "Planos con la alineación y las cotas resultantes.",
    ],
    needs: [
      "Referencia catastral y delimitación de la parcela.",
      "Acceso para el levantamiento topográfico.",
      "El expediente o proyecto al que va destinado, si existe.",
    ],
    receive: [
      "Documentación topográfica de la parcela respecto de la vía.",
      "Planos con alineación y rasantes.",
      "La resolución municipal, cuando el trámite lo requiera y el Ayuntamiento la emita.",
    ],
    faq: [
      {
        q: "¿Quién expide las alineaciones y rasantes oficiales?",
        a: "El Ayuntamiento competente, cuando el procedimiento lo requiere. Nuestro trabajo es preparar la documentación técnica y topográfica, presentar la solicitud y aportar la medición precisa de la parcela y de la vía.",
      },
      {
        q: "¿Es lo mismo la alineación que el lindero de mi finca?",
        a: "No. La alineación es la línea que marca la posición de la edificación respecto de la vía pública según el planeamiento; el lindero es el límite de tu propiedad. Pueden coincidir o no, y conviene conocer ambos antes de proyectar.",
      },
    ],
    relatedServices: ["cedula-urbanistica", "levantamiento-topografico", "licencia-obra-mayor"],
    relatedProblems: ["alineaciones-y-rasantes"],
    relatedGuides: ["que-son-alineaciones-y-rasantes"],
  },
  {
    slug: "comunicacion-previa-obra",
    ogImage: "/og/servicio-comunicacion-previa-obra.jpg",
    shortName: "Comunicación previa de obra",
    name: "Comunicación previa de obra",
    category: "edificaciones",
    seoTitle: "Comunicación previa de obra en Canarias | MideFinca",
    metaDescription:
      "Documentación técnica para actuaciones que puedan ejecutarse mediante comunicación previa en Canarias, comprobando qué procedimiento corresponde según la obra y la normativa municipal.",
    excerpt: "Documentación técnica para las obras que puedan tramitarse por comunicación previa, comprobando antes qué procedimiento corresponde.",
    h1: "Comunicación previa de obra en Canarias",
    intro:
      "Preparamos la documentación técnica necesaria para tramitar actuaciones que puedan ejecutarse mediante comunicación previa, comprobando previamente qué procedimiento corresponde según el tipo de obra y la normativa municipal aplicable.",
    when: [
      "Quieres hacer una reforma y no sabes si necesitas licencia o basta una comunicación previa.",
      "El Ayuntamiento te ha indicado que tu actuación puede tramitarse por comunicación previa.",
      "Vas a realizar una actuación menor y quieres presentar la documentación correctamente desde el principio.",
    ],
    solves:
      "No toda obra pequeña es comunicación previa ni toda reforma exige licencia: la calificación depende del tipo de actuación y de la ordenanza municipal de cada Ayuntamiento. Comprobarlo antes evita presentar el procedimiento equivocado, con sus retrasos y sus posibles consecuencias.",
    includes: [
      "Comprobación del procedimiento que corresponde a la actuación según la normativa municipal.",
      "Documentación técnica de la actuación: memoria, planos y medición, según el caso.",
      "Preparación y presentación de la comunicación previa.",
      "Presupuesto de ejecución de la obra cuando el trámite lo requiera.",
    ],
    needs: [
      "Descripción de la obra que quieres realizar.",
      "Referencia catastral y datos del inmueble.",
      "Planos o documentación existente, si la hay.",
    ],
    receive: [
      "La documentación técnica de la actuación.",
      "La comunicación previa presentada ante el Ayuntamiento.",
      "Confirmación del procedimiento aplicable a tu caso.",
    ],
    faq: [
      {
        q: "¿Obra menor y comunicación previa son lo mismo?",
        a: "No siempre. La calificación de una actuación como obra menor o su admisión por comunicación previa depende de la ordenanza de cada municipio y del tipo de obra. Por eso comprobamos qué procedimiento corresponde antes de presentar nada.",
      },
      {
        q: "¿Puedo empezar la obra al presentar la comunicación?",
        a: "Depende de la normativa municipal aplicable y del contenido de la comunicación. Te indicamos lo que procede en tu caso conforme a la ordenanza y a la respuesta municipal, sin dar por hecho un plazo concreto.",
      },
      {
        q: "¿Y si mi obra necesita licencia?",
        a: "Te lo decimos antes de empezar y preparamos entonces la documentación del procedimiento que corresponda, incluido el proyecto técnico cuando la actuación lo exija.",
      },
    ],
    relatedServices: ["licencia-obra-mayor", "cedula-urbanistica", "certificado-habitabilidad"],
    relatedProblems: ["que-permiso-necesito-reforma"],
    relatedGuides: ["comunicacion-previa-obra-cuando-se-utiliza"],
  },
  {
    slug: "licencia-obra-mayor",
    ogImage: "/og/servicio-licencia-obra-mayor.jpg",
    shortName: "Licencia de obra mayor",
    name: "Licencia de obra mayor",
    category: "edificaciones",
    seoTitle: "Licencia de obra mayor en Canarias | MideFinca",
    metaDescription:
      "Proyecto y documentación técnica para solicitar licencia urbanística de obra mayor en Canarias: obra nueva, ampliaciones, reformas, rehabilitaciones e intervenciones estructurales, según el caso.",
    excerpt: "Proyecto y documentación técnica para solicitar la licencia cuando tu actuación la requiere.",
    h1: "Licencia de obra mayor en Canarias",
    intro:
      "Redactamos el proyecto y la documentación técnica necesaria para solicitar una licencia urbanística de obra cuando la actuación requiere proyecto técnico y autorización municipal previa.",
    when: [
      "Vas a construir una edificación nueva.",
      "Quieres ampliar una construcción existente.",
      "Planeas una reforma que afecta a la estructura o a la configuración del inmueble.",
      "Vas a rehabilitar un edificio o a realizar intervenciones estructurales.",
      "El Ayuntamiento te ha indicado que tu actuación necesita licencia de obra mayor.",
    ],
    solves:
      "Cuando la actuación exige licencia, el expediente necesita un proyecto técnico completo y correctamente planteado: mediciones, planos, memoria y cumplimiento de la normativa aplicable. Un expediente bien preparado desde el inicio evita requerimientos de subsanación y retrasos en la tramitación.",
    includes: [
      "Estudio previo de la parcela y de la normativa urbanística aplicable.",
      "Levantamiento y medición del estado actual, cuando proceda.",
      "Redacción del proyecto técnico: memoria, planos y documentación exigible.",
      "Presentación de la solicitud de licencia ante el Ayuntamiento.",
      "Atención de los requerimientos técnicos del expediente, dentro del alcance contratado.",
    ],
    needs: [
      "La idea o el alcance de la obra que quieres realizar.",
      "Referencia catastral, escritura y datos del inmueble.",
      "Documentación previa existente: planos, licencias antiguas, proyectos.",
    ],
    receive: [
      "El proyecto técnico completo para la solicitud.",
      "La solicitud de licencia presentada con su documentación.",
      "La documentación del expediente, incluidas las subsanaciones atendidas cuando proceda.",
    ],
    faq: [
      {
        q: "¿Se garantiza la concesión de la licencia?",
        a: "No. La licencia la concede o deniega el Ayuntamiento conforme a la normativa urbanística. Nuestro trabajo es preparar un proyecto técnico correcto y completo, que es la condición para que el expediente pueda tramitarse favorablemente.",
      },
      {
        q: "¿Qué actuaciones necesitan licencia de obra mayor?",
        a: "Dependiendo del caso y de la ordenanza municipal: obra nueva, ampliaciones, determinadas reformas, rehabilitaciones, intervenciones estructurales y otras actuaciones sometidas a licencia. Lo comprobamos para tu actuación concreta antes de preparar nada.",
      },
      {
        q: "¿Cuánto tarda la licencia?",
        a: "Los plazos dependen del Ayuntamiento y del expediente. Lo que sí controlamos es que la documentación técnica entre completa y bien planteada, para no añadir demoras por nuestra parte.",
      },
    ],
    relatedServices: ["comunicacion-previa-obra", "cedula-urbanistica", "alineaciones-rasantes"],
    relatedProblems: ["obra-con-licencia-municipal"],
    relatedGuides: ["licencia-obra-mayor-documentacion"],
  },
  {
    slug: "cambio-de-uso-local-a-vivienda",
    shortName: "Cambio de uso",
    name: "Cambio de uso de local a vivienda",
    category: "edificaciones",
    seoTitle: "Cambio de uso de local a vivienda en Canarias | MideFinca",
    ogImage: "/og/servicio-cambio-de-uso-local-a-vivienda.jpg",
    metaDescription:
      "Estudio técnico de viabilidad y documentación para solicitar el cambio de uso de local a vivienda en Canarias: medición, huecos, ventilación y condiciones según la ordenanza municipal.",
    excerpt: "Comprobamos si tu local reúne las condiciones técnicas para poder solicitar su cambio de uso a vivienda.",
    h1: "Cambio de uso de local a vivienda en Canarias",
    intro:
      "Convertir un local en vivienda no empieza por la obra: empieza por saber si el espacio y la normativa lo permiten. Medimos el local, comprobamos sus condiciones (huecos, iluminación y ventilación natural, alturas, distribución y accesos) y preparamos la documentación técnica para solicitar el cambio de uso cuando el procedimiento municipal lo contemple.",
    when: [
      "Tienes o vas a comprar un local y quieres saber si podría convertirse en vivienda.",
      "Necesitas el informe técnico de viabilidad antes de decidir la inversión.",
      "El procedimiento municipal exige proyecto o documentación técnica para solicitar el cambio de uso.",
      "La comunidad de propietarios o los estatutos pueden verse afectados por el nuevo uso.",
    ],
    solves:
      "Muchas operaciones fracasan porque se compra o se obra antes de comprobar. El estudio previo responde con datos a la pregunta clave: si el local cumple las condiciones técnicas exigibles a una vivienda y si el planeamiento y la ordenanza de tu municipio admiten el cambio de uso.",
    includes: [
      "Visita y medición completa del local.",
      "Comprobación de huecos, iluminación y ventilación natural, alturas libres y distribución.",
      "Revisión del planeamiento y de la ordenanza municipal aplicable al cambio de uso.",
      "Informe técnico de viabilidad con las condiciones detectadas y los condicionantes.",
      "Documentación técnica para la solicitud del procedimiento que corresponda, según el municipio.",
    ],
    needs: [
      "Acceso al local.",
      "Referencia catastral y, si la tienes, escritura o nota simple.",
      "Estatutos de la comunidad de propietarios, si el local está en un edificio en régimen de propiedad horizontal.",
    ],
    receive: [
      "Informe técnico.",
      "Planos del local medido y, en su caso, de la propuesta de distribución.",
      "Relación de condicionantes técnicos y urbanísticos detectados.",
    ],
    faq: [
      {
        q: "¿Cualquier local puede convertirse en vivienda?",
        a: "No. Depende del planeamiento municipal, de la ordenanza correspondiente, de las condiciones físicas del local (huecos, ventilación y alturas) y, en su caso, de la comunidad de propietarios. Por eso el primer paso es el estudio de viabilidad, no la obra.",
      },
      {
        q: "¿El informe de viabilidad garantiza que me concederán el cambio de uso?",
        a: "No. El informe documenta las condiciones técnicas y los condicionantes del caso; la resolución del procedimiento corresponde al Ayuntamiento. Lo que evita es invertir a ciegas.",
      },
      {
        q: "¿Qué trámite hace falta?",
        a: "Depende del municipio y del caso: puede requerir desde una declaración responsable o comunicación previa hasta licencia urbanística con proyecto técnico. Tras la visita y la revisión normativa te indicamos qué procedimiento aplica a tu local.",
      },
    ],
    relatedServices: ["certificado-habitabilidad", "licencia-obra-mayor", "certificado-superficies"],
    relatedProblems: ["convertir-local-en-vivienda"],
    relatedGuides: ["cambio-de-uso-local-a-vivienda-canarias"],
  },
];
